import { show } from "@ebay/nice-modal-react";
import { useAtomValue } from "jotai";
import { type MouseEvent, useEffect, useState } from "react";
import { AI_AGENT_STATIC_PATH_V2 } from "@/api/staticPaths";
import { canv } from "@/canv/global";
import { AiAssistantIcon } from "@/icons/widget-menu";
import { buttonDisabledAtom } from "@/interface/AppStore";
import { AiAgentFreeModal } from "@/interface/ui/modals/AiAgentFreeModal";
import { useLottie } from "@/shared/lib/hooks/useLottie";
import type { ActiveSubMenuState, MenuOptionResult, SetActiveSubMenu } from "../../types";
import styles from "../../WidgetMenu.module.css";

interface UseAiAssistantButtonProps {
    activeSubMenu: ActiveSubMenuState | null;
    setActiveSubMenu: SetActiveSubMenu;
}

export function useAiAssistantButton({
    activeSubMenu,
    setActiveSubMenu,
}: UseAiAssistantButtonProps): MenuOptionResult {
    const { aiAssistant: isDisabled } = useAtomValue(buttonDisabledAtom);

    const [data, setData] = useState<any>(undefined);

    const { View, play, pause } = useLottie(
        {
            animationData: data,
            loop: true,
            autoplay: false,
        },
        { display: "flex", justifyContent: "center", alignItems: "center", width: 20, height: 20 },
    );

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        const isProfessional = canv.getInitDataProp("isProfessional");

        if (isProfessional) {
            const button = event.currentTarget;

            setActiveSubMenu((prev) =>
                prev?.name === "aiAssistant" ? null : { name: "aiAssistant", anchor: button },
            );
        } else {
            show(AiAgentFreeModal);
        }
    };

    const content = (
        <>
            {View}
            {!isDisabled && <AiAssistantIcon className={styles.aiIcon} />}
        </>
    );
    const tippyContent = canv.getInitDataProp("isProfessional")
        ? "Ваш помощник"
        : "Ваш помощник (тариф Pro)";

    const isActive = activeSubMenu?.name === "aiAssistant";

    useEffect(() => {
        fetch(`${canv.host}${AI_AGENT_STATIC_PATH_V2}button-animation.json`)
            .then((response) => response.json())
            .then(setData);
    }, []);

    useEffect(() => {
        if (isDisabled) {
            play();
        } else {
            pause();
        }
    }, [isDisabled, pause, play]);

    return {
        content,
        handleClick,
        isActive,
        isDisabled,
        tippyContent,
    };
}
